/**
 * 動画メタ（一覧）。取り込み済みの動画を並べ、まとめて読み取りを始める。
 *
 * ## 処理していないことと、処理して何も出なかったことを分ける
 *
 * 1 本ずつの行には `processState` から作った状態だけを出す。
 * 「まだ処理していません」は**処理の前**の断りで、読み取った結果が
 * 空だったこととは別物である（見分けは詳細の画面で付ける）。
 *
 * ## 対象外を、黙って数から消さない（ADR-13 / DEC-3・ADR 0003）
 *
 * mp4 の原本が無くても HLS（m3u8）があれば処理の対象である（結合してから読む）。
 * 対象外は、処理に渡す URL が 1 つも無い動画で、その本数と理由を上に出す。
 * **原本が 200 MB を超えるものは数えていない**（大きさを持っている列が無い）ので、
 * 数えていないと書く。0 本とは書かない。
 *
 * ## 1 本ずつの操作は行の「…」に畳む
 *
 * 詳細を開く・処理する・もう一度処理する・m3u8 から処理する を、行の右上の
 * メニューに置く。押せないものは押せない形で残し、理由を添える
 * （押しても何も起きない導線を出さない — ADR 0001）。
 *
 * ## 置いていない枠
 *
 * - **再生数と、再生数の並べ替え**。動画 1 本に紐づけられる取り込み元が無い
 * - **終わるまでの時間**。1 度も測っていないので、当てずっぽうの秒数を置かない
 *
 * ## 新しい動画の自動処理（W25 / FR27.4）
 *
 * 一覧の上に設定の区画を置く。**既定は「切」**で、設定した会社だけが動く。
 * 最後に検知した時刻が空なのは「0 本だった」ではなく「まだ自動では処理して
 * いない」である（`VideoMetaAutoIngestPanel`）。
 */

import { useCallback, useState } from "react";
import { Link } from "react-router";
import { useQuery } from "urql";
import { Button as AntButton, Dropdown, type MenuProps } from "antd";
import { MoreOutlined } from "@ant-design/icons";
import {
  Card,
  ErrorState,
  LoadingState,
  PageHeader,
  Tag,
  VideoMetaDocsDocument,
  type LinkState,
  type TagTone,
  type VideoProcessState,
} from "visumo-data-agent-web-shared";

import { formatJst } from "../format-time";
import { HelpIcon } from "../help/HelpIcon";
import { useStoredChoice } from "../stored-choice";
import { VideoMetaAutoIngestPanel } from "./VideoMetaAutoIngestPanel";
import { VideoMetaBatchPanel } from "./VideoMetaBatchPanel";
import { unavailableMediaUrl } from "./media-url";
import {
  processActionFor,
  processLabel,
  VideoProcessConfirm,
} from "./VideoProcessAction";

/** 処理の状態の表示名。内部の値そのものは画面に出さない。 */
const PROCESS_STATE_LABELS: Record<string, string> = {
  PENDING: "まだ処理していません",
  RUNNING: "処理中",
  DONE: "処理済み",
  FAILED: "失敗",
  SKIPPED: "対象外",
};

/** 1 本ずつの行で何が読めるかを、画面の上で 1 度だけ断る。 */
const LIST_NOTE =
  "動画の中身（区間・話題・テロップ・文字起こし・商品候補）は、読み取りが済んだ動画だけで出ます。" +
  "1 本開くと、その動画について分かっていることが読めます。";

/** 状態ごとの見た目。**状態の文言と色を 1 か所で決める**。 */
const STATE_TONES: Record<string, TagTone> = {
  PENDING: "info",
  RUNNING: "info",
  DONE: "ok",
  FAILED: "danger",
  SKIPPED: "neutral",
};

interface VideoRow {
  readonly id: string;
  readonly ugcId: string;
  readonly seq: number;
  readonly videoUrl: string | null;
  readonly hlsUrl: string | null;
  readonly thumbnailUrl: string | null;
  readonly durationSec: number | null;
  readonly postedAt: string | null;
  readonly processState: string;
  readonly skipReason: string | null;
  readonly processNote: string | null;
  readonly linkState: string;
}

/** 商品の結びつきの表示名。内部の値そのものは画面に出さない。 */
const LINK_STATE_LABELS: Record<string, string> = {
  UNLINKED: "商品候補なし",
  SUGGESTED: "商品候補あり",
  CONFIRMED: "商品を確定済み",
};

function stateLabel(state: string): string {
  return PROCESS_STATE_LABELS[state] ?? state;
}

/** 尺は「1 分 23 秒」のように読める形で出す（秒の生値を並べない）。 */
function formatDuration(seconds: number | null): string | null {
  if (seconds === null) return null;
  const whole = Math.round(seconds);
  const minutes = Math.floor(whole / 60);
  const rest = whole % 60;
  if (minutes === 0) return `${String(rest)} 秒`;
  return `${String(minutes)} 分 ${String(rest)} 秒`;
}

/**
 * 動画の見出し。**内部の長い識別子は出さない**。
 * 投稿のなかの何番目の要素か、という人が読める形にする。
 */
function videoTitle(row: VideoRow): string {
  return `投稿 ${row.ugcId} の ${String(row.seq)} 番目の動画`;
}

/** サムネイルが出せるか。無い・読めない URL では灰色の枠に置き換える。 */
function VideoThumb({ url }: { url: string | null }) {
  const [failed, setFailed] = useState(false);
  if (url === null || unavailableMediaUrl(url) || failed) {
    return (
      <div className="vd-video-thumb vd-video-thumb--none">サムネイルなし</div>
    );
  }
  return (
    <img
      className="vd-video-thumb"
      src={url}
      alt=""
      loading="lazy"
      width={120}
      height={68}
      onError={() => {
        setFailed(true);
      }}
    />
  );
}

/**
 * 1 本ずつの操作。行の右上の「…」から開く。
 *
 * 押せない操作は**消さずに押せない形で**置き、理由を `title` で出す
 * （なぜ押せないのかが分からないまま、メニューから項目が消えないようにする）。
 * 押せない理由も、押したら何をするかも `processActionFor` だけが決める。
 */
function VideoRowMenu({
  row,
  title,
  started,
  onProcess,
}: {
  row: VideoRow;
  title: string;
  started: boolean;
  onProcess: () => void;
}) {
  const action = processActionFor(row);
  const processItem: NonNullable<MenuProps["items"]>[number] = started
    ? {
        key: "process",
        label: "処理する",
        disabled: true,
        title: "読み取りを受け付けました。終わると状態が変わります。",
      }
    : action.kind === "available"
      ? { key: "process", label: processLabel(action, "list") }
      : {
          key: "process",
          label: "処理する",
          disabled: true,
          title: action.reason,
        };
  return (
    <Dropdown
      trigger={["click"]}
      menu={{
        items: [
          {
            key: "open",
            label: (
              <Link to={`/video-meta/${encodeURIComponent(row.id)}`}>
                詳細を開く
              </Link>
            ),
          },
          processItem,
        ],
        onClick: ({ key }) => {
          if (key === "process") onProcess();
        },
      }}
    >
      <AntButton
        type="text"
        size="small"
        icon={<MoreOutlined />}
        aria-label={`${title} の操作`}
      />
    </Dropdown>
  );
}

function VideoRowCard({
  row,
  onStarted,
}: {
  row: VideoRow;
  onStarted: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const [started, setStarted] = useState(false);
  const duration = formatDuration(row.durationSec);
  const posted = formatJst(row.postedAt);
  const title = videoTitle(row);
  const action = processActionFor(row);

  return (
    <section
      aria-label={title}
      className="vd-video-row"
      style={{ borderTop: "1px solid var(--vd-border)", paddingTop: 12 }}
    >
      <VideoThumb url={row.thumbnailUrl} />
      <div className="vd-video-row-body">
        <div className="vd-row" style={{ justifyContent: "space-between" }}>
          <div className="vd-card-title">
            <Link to={`/video-meta/${encodeURIComponent(row.id)}`}>
              {title}
            </Link>
            <Tag tone={STATE_TONES[row.processState]}>
              {row.processState === "SKIPPED"
                ? "対象外"
                : stateLabel(row.processState)}
            </Tag>
            {/* 商品の結びつきは、読み取りが済んだ動画でだけ意味がある */}
            {row.processState === "DONE" ? (
              <Tag tone={row.linkState === "CONFIRMED" ? "ok" : "neutral"}>
                {LINK_STATE_LABELS[row.linkState] ?? row.linkState}
              </Tag>
            ) : null}
          </div>
          <div className="vd-row">
            <span className="vd-stat-note">
              {duration === null ? "尺は取り込めていません" : duration}
            </span>
            <VideoRowMenu
              row={row}
              title={title}
              started={started}
              onProcess={() => {
                setConfirming(true);
              }}
            />
          </div>
        </div>

        <p className="vd-stat-note" style={{ margin: "4px 0 0" }}>
          {posted === null
            ? "投稿された時刻は取り込めていません。"
            : `投稿 ${posted}`}
        </p>

        {/* 失敗・対象外の 1 行は、黙って消さずに一覧にも出す */}
        {row.processNote === null ? null : (
          <p className="vd-stat-note" style={{ margin: "4px 0 0" }}>
            {row.processNote}
          </p>
        )}

        {started ? (
          <p
            className="vd-stat-note"
            style={{ margin: "8px 0 0" }}
            role="status"
          >
            この動画の読み取りを受け付けました。終わると状態が変わります。
          </p>
        ) : confirming && action.kind === "available" ? (
          <VideoProcessConfirm
            videoId={row.id}
            title={title}
            action={action}
            onStarted={() => {
              setStarted(true);
              setConfirming(false);
              onStarted();
            }}
            onCancel={() => {
              setConfirming(false);
            }}
          />
        ) : null}
      </div>
    </section>
  );
}

/**
 * 状態で絞る選択肢。**API が実際に絞れる状態だけを出す。**
 *
 * 「処理済み」「失敗」は読解の結果にしかないので、API は引く向きを逆にして
 * 絞る。置いてよいのは**絞れると決まっているものだけ**である。置いてしまうと、
 * 絞ったつもりで未処理の動画が「処理済み」として並ぶ（2026-09-20 に dev で
 * 実際に出た）。
 *
 * 「実行中」は置かない。実行中であることは読解の結果に記録されないので、
 * 渡すと断られる。
 */
const STATE_CHOICES = [
  { value: "ALL", label: "すべて" },
  { value: "PENDING", label: "まだ処理していません" },
  { value: "SKIPPED", label: "対象外" },
  { value: "DONE", label: "処理済み" },
  { value: "FAILED", label: "失敗" },
] as const;

/**
 * 商品候補の有無で絞る選択肢。
 *
 * **読解していない動画は 1 本も返らない。** 結びつきは読解の結果であって、
 * まだ読んでいない動画には「候補なし」も含めて何も記録が無い。
 */
const LINK_CHOICES = [
  { value: "ALL", label: "すべて" },
  { value: "UNLINKED", label: "商品候補なし" },
  { value: "SUGGESTED", label: "商品候補あり" },
  { value: "CONFIRMED", label: "商品を確定済み" },
] as const;

type LinkChoice = (typeof LINK_CHOICES)[number]["value"];

const LINK_VALUES = LINK_CHOICES.map((choice) => choice.value);

const LINK_KEY = "videoMeta.linkState";

type StateChoice = (typeof STATE_CHOICES)[number]["value"];

function toStateChoice(value: string): StateChoice {
  const found = STATE_CHOICES.find((choice) => choice.value === value);
  return found?.value ?? "ALL";
}

const STATE_VALUES = STATE_CHOICES.map((choice) => choice.value);

function toLinkChoice(value: string): LinkChoice {
  return LINK_CHOICES.find((choice) => choice.value === value)?.value ?? "ALL";
}

const ORDER_VALUES = ["DURATION_DESC", "POSTED_AT_DESC"] as const;

type OrderChoice = (typeof ORDER_VALUES)[number];

/**
 * 覚えておく選択の名前。**値そのものは `stored-choice` が検める**ので、
 * 選択肢を減らしたあとに古い値が残っていても既定へ戻る。
 */
const ORDER_KEY = "videoMeta.order";
const STATE_KEY = "videoMeta.processState";

export function VideoMetaPage() {
  // 1 本開いて戻ると選び直しになっていたので、この端末に覚えておく
  const [stateFilter, setStateFilter] = useStoredChoice<StateChoice>(
    STATE_KEY,
    STATE_VALUES,
    "ALL",
  );
  const [order, setOrder] = useStoredChoice<OrderChoice>(
    ORDER_KEY,
    ORDER_VALUES,
    "DURATION_DESC",
  );
  const [linkFilter, setLinkFilter] = useStoredChoice<LinkChoice>(
    LINK_KEY,
    LINK_VALUES,
    "ALL",
  );

  const [listResult, refetchList] = useQuery({
    query: VideoMetaDocsDocument,
    variables: {
      first: 20,
      order,
      ...(stateFilter === "ALL" && linkFilter === "ALL"
        ? {}
        : {
            filter: {
              ...(stateFilter === "ALL"
                ? {}
                : { processState: stateFilter as VideoProcessState }),
              ...(linkFilter === "ALL"
                ? {}
                : { linkState: linkFilter as LinkState }),
            },
          }),
    },
  });
  // 読み取りが終わったら一覧を引き直す（状態が変わっているため）
  const refreshList = useCallback(() => {
    refetchList({ requestPolicy: "network-only" });
  }, [refetchList]);

  if (listResult.fetching && listResult.data === undefined) {
    return <LoadingState />;
  }
  if (listResult.error !== undefined || listResult.data === undefined) {
    return (
      <ErrorState
        description={listResult.error?.message}
        onRetry={() => refetchList({ requestPolicy: "network-only" })}
      />
    );
  }

  const rows = listResult.data.videoMetaDocs.edges.map(
    (edge) => edge.node as VideoRow,
  );
  const hasNextPage = listResult.data.videoMetaDocs.pageInfo.hasNextPage;

  return (
    <div className="vd-page">
      <PageHeader
        title="動画メタ"
        description="自社の動画を 1 本ごとに並べます。読み取りが済んだ動画では、区間・話題・テロップ・文字起こし・商品候補が読めます。"
        titleExtra={<HelpIcon slug="video-meta/list" />}
      />

      <div className="vd-stack">
        <VideoMetaAutoIngestPanel />

        <VideoMetaBatchPanel onFinished={refreshList} />

        <Card
          title="動画"
          note={LIST_NOTE}
          extra={
            <HelpIcon
              slug="video-meta/list"
              anchor="並べ替えと絞り込みは覚えます"
            />
          }
        >
          <div className="vd-row" style={{ marginBottom: 12 }}>
            <label>
              <span style={{ marginRight: 6 }}>並べ替え</span>
              <select
                aria-label="並べ替え"
                value={order}
                onChange={(event) => {
                  setOrder(
                    event.target.value === "POSTED_AT_DESC"
                      ? "POSTED_AT_DESC"
                      : "DURATION_DESC",
                  );
                }}
              >
                <option value="DURATION_DESC">尺の長い順</option>
                <option value="POSTED_AT_DESC">投稿の新しい順</option>
              </select>
            </label>
            <label>
              <span style={{ marginRight: 6 }}>状態で絞る</span>
              <select
                aria-label="状態で絞る"
                value={stateFilter}
                onChange={(event) => {
                  setStateFilter(toStateChoice(event.target.value));
                }}
              >
                {STATE_CHOICES.map((choice) => (
                  <option key={choice.value} value={choice.value}>
                    {choice.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span style={{ marginRight: 6 }}>商品候補で絞る</span>
              <select
                aria-label="商品候補で絞る"
                value={linkFilter}
                onChange={(event) => {
                  setLinkFilter(toLinkChoice(event.target.value));
                }}
              >
                {LINK_CHOICES.map((choice) => (
                  <option key={choice.value} value={choice.value}>
                    {choice.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {rows.length === 0 ? (
            <p aria-label="動画の一覧">
              {stateFilter === "ALL" && linkFilter === "ALL"
                ? "取り込めている動画がありません。"
                : "この絞り込みにあたる動画は、この範囲ではありませんでした。"}
            </p>
          ) : (
            <div className="vd-stack">
              {rows.map((row) => (
                <VideoRowCard key={row.id} row={row} onStarted={refreshList} />
              ))}
            </div>
          )}

          {hasNextPage ? (
            <p className="vd-stat-note" style={{ margin: "12px 0 0" }}>
              まだ続きがあります。いまは先頭の 20 本だけを出しています。
            </p>
          ) : null}
        </Card>
      </div>
    </div>
  );
}

export { formatDuration, stateLabel, videoTitle };
